/**
 * 시간 변환 관련 유틸 함수 모듈
 * @module core/time/conversion
 */
import dayjs from 'dayjs';
import type { TimeRangeInfo, DynamicTimeUnit } from '@/common/utils/types';
import { isNil } from 'lodash-es';
import { parsePeriod } from './parse';
import {
  MS_TO_SEC,
  NS_TO_SEC,
  ONE_DAY,
  ONE_HOUR,
  ONE_MINUTE,
  ONE_MONTH,
  ONE_SECOND,
  ONE_WEEK,
  ONE_YEAR,
  TIME_SHORT_UNITS,
  US_TO_SEC,
} from '@/common/utils/define';
import { convertMsToSec } from '../number/conversion';
import { roundToDigit, roundToDigitNumber, roundToDigitValue } from '../number/format';

/**
 * 특정 Time에서 +- rangeMs의 fromTime toTime 객체로 변환
 */
export const convertTimeToRangeTime = (collectTime: string, rangeMs = 300000): TimeRangeInfo => {
  if (collectTime) {
    const collectTimeUnixTimestamp = +dayjs(collectTime);
    return {
      fromTime: dayjs(collectTimeUnixTimestamp - rangeMs).format('YYYY-MM-DD HH:mm:ss'),
      toTime: dayjs(collectTimeUnixTimestamp + rangeMs).format('YYYY-MM-DD HH:mm:ss'),
    };
  }
  return {
    fromTime: '',
    toTime: '',
  };
};

/**
 * 시간 단위를 초로 변환
 * @param {number} value - api 데이터 값
 * @param {string} unit - 수집 단위 'ns' | 'us' | 'ms' | 'sec' | 'min'
 * @param {string} returnType - 반환 타입 'string' | 'number' (기본값: 'number')
 * @returns {string | number | null} 변환된 값
 */
export const convertTimeToSec = (
  value: number | string,
  unit: string = 'ms', // 'ns' | 'us' | 'ms' | 'sec' | 'min'
  returnType?: string,
): string | number | null => {
  if (isNil(value)) {
    return null;
  }

  let numValue: number;
  if (typeof value === 'string') {
    numValue = +(value as string).replace(/,/g, '');
  } else {
    numValue = value;
  }

  if (unit === 'ms') {
    return returnType === 'string'
      ? convertMsToSec(numValue, { digits: 3, unit: false })
      : +convertMsToSec(numValue, { digits: 3, unit: false }).replace(/,/g, '');
  }

  const UNIT_MAP = {
    ns: NS_TO_SEC,
    us: US_TO_SEC,
    ms: MS_TO_SEC,
    sec: 1,
    min: 1 / 60,
  };
  const baseUnit = UNIT_MAP[unit];
  if (isNil(baseUnit)) {
    // cannot convert
    return null;
  }
  return returnType === 'string'
    ? roundToDigitValue(numValue / baseUnit, 3)
    : roundToDigit(numValue / baseUnit, 3);
};

/**
 * 밀리초를 동적 시간 단위로 변환
 * @param ms - 변환할 밀리초 값
 * @param options - 변환 옵션
 * @param options.unitList - 사용할 시간 단위 리스트 (기본값: TIME_SHORT_UNITS)
 * @param options.decimal - 소수점 자리수
 * @param options.valueType - 반환 타입 ('number' | 'string', 기본값: 'string')
 * @returns 변환된 값과 단위
 */
export const convertToDynamicTime = (
  ms: number,
  {
    unitList,
    decimal,
    valueType = 'string',
  }: { unitList?: DynamicTimeUnit[]; decimal?: number; valueType?: 'number' | 'string' } = {},
): {
  value: string | number;
  unit: DynamicTimeUnit;
} => {
  const defaultUnitList = unitList || TIME_SHORT_UNITS;
  const MS_BY_UNIT = {
    ms: 1,
    s: ONE_SECOND,
    m: ONE_MINUTE,
    h: ONE_HOUR,
    d: ONE_DAY,
    w: ONE_WEEK,
    M: ONE_MONTH,
    y: ONE_YEAR,
  } as const;

  let unitIdx = 1;
  let unit = defaultUnitList[unitIdx];
  while (unitIdx < defaultUnitList.length && ms / MS_BY_UNIT[unit] >= 1) {
    unitIdx++;
    unit = defaultUnitList[unitIdx];
  }

  const lastUnitIdx = unitIdx - 1;
  const lastUnit = defaultUnitList[lastUnitIdx] as DynamicTimeUnit;
  const converted =
    valueType === 'string'
      ? roundToDigit(ms / MS_BY_UNIT[lastUnit], decimal || 0)
      : roundToDigitNumber(ms / MS_BY_UNIT[lastUnit], decimal || 0);

  return {
    value: converted,
    unit: lastUnit,
  };
};

/**
 * 복합 period를 지원하는 period 중 가장 가까운 값으로 변환
 * @param period - 변환할 period (예: 'p23h 59m 49s')
 * @param supportedPeriods - 지원하는 period 목록 (예: ['p5m', 'p10m', 'p1h', 'p1d'])
 * @param strategy - 변환 전략 ('round': 반올림, 'ceil': 올림, 'floor': 내림)
 * @returns 가장 가까운 지원 period
 */
export const findNearestSupportedPeriod = (
  period: string,
  supportedPeriods: string[],
  strategy: 'round' | 'ceil' | 'floor' = 'round',
): string => {
  if (supportedPeriods.length === 0) {
    return period;
  }

  const targetSeconds = parsePeriod(period).offset;

  // 지원 period들을 초 단위로 변환하고 정렬
  const sortedPeriods = supportedPeriods
    .map((p) => ({ period: p, seconds: parsePeriod(p).offset }))
    .filter((p) => p.seconds > 0)
    .sort((a, b) => a.seconds - b.seconds);

  if (sortedPeriods.length === 0) {
    return period;
  }

  // 최소/최대 범위 처리
  const minPeriod = sortedPeriods[0];
  const maxPeriod = sortedPeriods[sortedPeriods.length - 1];

  if (targetSeconds <= minPeriod.seconds) {
    return minPeriod.period;
  }
  if (targetSeconds >= maxPeriod.seconds) {
    return maxPeriod.period;
  }

  // 이진 탐색으로 target이 들어갈 위치 찾기
  let left = 0;
  let right = sortedPeriods.length - 1;

  while (left < right - 1) {
    const mid = Math.floor((left + right) / 2);
    if (sortedPeriods[mid].seconds <= targetSeconds) {
      left = mid;
    } else {
      right = mid;
    }
  }

  const lower = sortedPeriods[left];
  const upper = sortedPeriods[right];

  // 전략에 따른 선택
  if (strategy === 'floor') {
    return lower.period;
  }
  if (strategy === 'ceil') {
    return upper.period;
  }

  // round: 더 가까운 쪽 선택
  const lowerDiff = targetSeconds - lower.seconds;
  const upperDiff = upper.seconds - targetSeconds;

  return lowerDiff <= upperDiff ? lower.period : upper.period;
};
